import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import { useApi, useAuth } from "../contexts/AuthContext";
import { useDynamicT } from "../contexts/DynamicTranslationContext";
import { Layout } from "./Layout";
import { PhotoDisplay } from "./media";

// ============= Design tokens =============
const C = {
  black: "#0F0F0E",
  offWhite: "#FAF8F4",
  stone: "#E8E1D5",
  stoneDark: "#D9CFBF",
  mineral: "#6B6B68",
  mineralLight: "#9A968D",
  bronze: "#A8804E",
  bronzeDark: "#8B6F47",
};

const POLE_KEYS = ["habitation", "hotelerie", "commercial", "promotion", "bureaux", "plage_restaurant", "fonds_commerce"];
const poleLabel = (pole, t) => (pole ? t(`poles.${pole}`, { defaultValue: pole }) : "");

const POLE_COLORS = [
  C.black, C.bronze, C.bronzeDark, C.mineral,
  C.mineralLight, C.stoneDark, "#4A4A47",
];

const STATUS_ORDER = ["etude", "offre", "sous_compromis", "acte"];
const statusLabel = (st, t) => (st ? t(`status.${st}`, { defaultValue: st }) : "");

// ============= Formatters =============
const fmtEUR = (v) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(v || 0);

const fmtEURcompact = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toLocaleString("de-DE", { maximumFractionDigits: 1 })} M €`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toLocaleString("de-DE", { maximumFractionDigits: 0 })} k €`;
  return fmtEUR(n);
};

const fmtPct = (v) => `${(v || 0).toLocaleString("de-DE", { maximumFractionDigits: 1 })} %`;

const fmtInt = (v) => (v || 0).toLocaleString("de-DE");

// Compute an asset's annual rental income following the backend rules:
// - habitation & promotion poles (or rent_type=saisonnier) use seasonal_revenue
// - all other poles use annual_rent
// Prevents double-counting when both fields are populated on legacy data.
const assetAnnualIncome = (a) => {
  if (!a) return 0;
  const isSeasonal = a.rent_type === "saisonnier" || a.pole === "habitation" || a.pole === "promotion";
  return isSeasonal ? (a.seasonal_revenue || 0) : (a.annual_rent || 0);
};

// Extract city from address (last token, stripped of postal code)
const extractCity = (addr) => {
  if (!addr) return null;
  const parts = addr.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  const last = parts[parts.length - 1] || addr;
  return last.replace(/\b\d{4,6}\b/g, "").trim();
};

// ============= Motion variants =============
const easeOut = [0.16, 1, 0.3, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: easeOut, delay: i * 0.08 },
  }),
};

// ============= Photo URL resolver =============
const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Extract file id from any URL variant and rebuild with current backend
const FILE_ID_REGEX = /\/api\/files\/([a-zA-Z0-9_-]+)/;
const resolvePhoto = (photo) => {
  if (!photo) return null;
  const match = String(photo).match(FILE_ID_REGEX);
  if (match) return `${BACKEND}/api/files/${match[1]}`;
  if (photo.startsWith("http")) return photo;
  return photo;
};
const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1505843513577-22bb7d21e455?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZWFsJTIwZXN0YXRlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MzQxMTYwMHww&ixlib=rb-4.1.0&q=85";

const HERO_BRAND_IMAGE = "/branding/kempf-hero-mountain-v2.png";

// Return first available photo across all photo fields of an asset / dossier
const pickPhoto = (obj) => {
  if (!obj) return null;
  const candidates = [
    obj.main_photo,
    Array.isArray(obj.photos_after_works) ? obj.photos_after_works[0] : null,
    Array.isArray(obj.photos_before_works) ? obj.photos_before_works[0] : null,
    Array.isArray(obj.photos) ? obj.photos[0] : null,
  ].filter(Boolean);
  return candidates[0] || null;
};

// ============================================================
// COMPONENT
// ============================================================

// Small inline component that runs a dynamic translation on user-generated text
const DT = ({ children }) => {
  const translated = useDynamicT(typeof children === "string" ? children : null);
  if (typeof children !== "string") return children;
  return translated;
};

export default function DashboardPremium() {
  const api = useApi();
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [assets, setAssets] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [valuationHistory, setValuationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [strategicModalOpen, setStrategicModalOpen] = useState(false);
  const [strategicDraft, setStrategicDraft] = useState(new Set());
  const [savingStrategic, setSavingStrategic] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, a, d, vh] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/assets"),
          api.get("/dossiers").catch(() => ({ data: [] })),
          api.get("/dashboard/valuation-history").catch(() => ({ data: [] })),
        ]);
        setStats(s.data);
        setAssets(a.data || []);
        setDossiers(d.data || []);
        setValuationHistory(vh.data || []);
      } catch (e) {
        console.error(e);
        toast.error(t("dashboard.load_error"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Derived: patrimonial assets with weighted values
  const derived = useMemo(() => {
    const patrimonial = assets.filter(
      (a) => a.status === "patrimonial" || a.status === "marchand_de_bien"
    );
    const share = (a) => (a.ownership_percentage ?? 100) / 100;
    const totalAcquisition = patrimonial.reduce(
      (s, a) => s + (a.acquisition_price || 0) * share(a),
      0
    );
    const totalValuation = patrimonial.reduce(
      (s, a) => s + (a.current_valuation || 0) * share(a),
      0
    );
    // Full 100 % valuation (group assets, ignoring ownership percentage)
    const totalValuationFull = patrimonial.reduce(
      (s, a) => s + (a.current_valuation || 0),
      0
    );
    // Full 100 % annual rent (per-asset by pole, no double counting)
    const totalRentFull = patrimonial.reduce(
      (s, a) => s + assetAnnualIncome(a),
      0
    );
    const unrealizedGain = totalValuation - totalAcquisition;
    const totalEquity = patrimonial.reduce((s, a) => s + (a.equity || 0), 0);
    const unrealizedGainPct =
      totalAcquisition > 0 ? (unrealizedGain / totalAcquisition) * 100 : 0;
    // Top valued assets — use user's manual "strategic" selection if any, else fallback to top by valuation
    const strategic = patrimonial.filter((a) => a.is_strategic);
    const sortedByValuation = [...patrimonial].sort(
      (a, b) =>
        (b.current_valuation || 0) * share(b) -
        (a.current_valuation || 0) * share(a)
    );
    const topAssets = strategic.length > 0
      ? [...strategic].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      : sortedByValuation.slice(0, 6);
    // Unique cities: all assets (incl. sold) + open dossiers
    const cities = new Set(
      [
        ...assets.map((a) => a.city || extractCity(a.address)),
        ...(dossiers || []).map((d) => d.city || extractCity(d.address)),
      ].filter(Boolean)
    );
    return { patrimonial, totalAcquisition, totalValuationFull, totalRentFull, unrealizedGain, unrealizedGainPct, topAssets, cities, totalEquity };
  }, [assets, dossiers]);

  const heroAsset = assets[0] || derived.topAssets[0] || null;
  const heroPhoto = heroAsset ? resolvePhoto(pickPhoto(heroAsset)) : null;

  // KPIs
  const totalValuation = stats?.total_valuation || 0;
  const totalRent =
    (stats?.total_annual_rent || 0) +
    (stats?.total_hotel_rent || 0) +
    (stats?.total_seasonal_revenue || 0);
  const avgYield = useMemo(() => {
    // Weighted yield across categories with non-zero acquisition base
    const parts = [
      { y: stats?.average_yield_annual, r: stats?.total_annual_rent },
      { y: stats?.average_yield_hotel, r: stats?.total_hotel_rent },
      { y: stats?.average_yield_seasonal, r: stats?.total_seasonal_revenue },
    ].filter((p) => p.y && p.r);
    const totalR = parts.reduce((s, p) => s + p.r, 0);
    if (!totalR) return 0;
    return parts.reduce((s, p) => s + p.y * p.r, 0) / totalR;
  }, [stats]);

  // Commercial-only average yield (annual rent / acquisition, weighted by acquisition base)
  const commercialYield = useMemo(() => {
    const commercial = derived.patrimonial.filter((a) => a.pole === "commercial");
    const totalAcq = commercial.reduce((s, a) => s + (a.acquisition_price || 0), 0);
    const totalRent = commercial.reduce((s, a) => s + (a.annual_rent || 0), 0);
    return totalAcq > 0 ? (totalRent / totalAcq) * 100 : 0;
  }, [derived.patrimonial]);

  const totalDebt = totalValuation * ((stats?.average_ltv || 0) / 100);

  // Pole distribution
  const poleData = useMemo(() => {
    if (!stats?.by_pole) return [];
    return Object.entries(stats.by_pole)
      .map(([k, v]) => ({
        pole: poleLabel(k, t),
        key: k,
        value: v.valuation || 0,
        count: v.count || 0,
        yield: v.yield || 0,
        ltv: v.ltv || 0,
      }))
      .filter((p) => p.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [stats, t]);

  // Dossiers grouped by status
  const pipeline = useMemo(() => {
    const g = { etude: [], offre: [], sous_compromis: [], acte: [] };
    (dossiers || []).forEach((d) => {
      if (g[d.status]) g[d.status].push(d);
    });
    return g;
  }, [dossiers]);

  // Save strategic selection
  const toggleStrategicDraft = (id) => {
    setStrategicDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const saveStrategicSelection = async () => {
    setSavingStrategic(true);
    try {
      // Only update assets whose state actually changes
      const updates = assets
        .filter((a) => Boolean(a.is_strategic) !== strategicDraft.has(a.id))
        .map((a) => api.put(`/assets/${a.id}`, { is_strategic: strategicDraft.has(a.id) }));
      if (updates.length === 0) {
        setStrategicModalOpen(false);
        return;
      }
      await Promise.all(updates);
      // Refresh assets
      const res = await api.get("/assets");
      setAssets(res.data || []);
      toast.success(`${updates.length} actif${updates.length > 1 ? "s" : ""} mis à jour`);
      setStrategicModalOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingStrategic(false);
    }
  };

  // Valuation history compressed for chart (monthly, sample every N)
  const chartData = useMemo(() => {
    if (!valuationHistory?.length) return [];
    // If very long, keep about 60 points
    const step = Math.max(1, Math.floor(valuationHistory.length / 60));
    const pts = [];
    for (let i = 0; i < valuationHistory.length; i += step) pts.push(valuationHistory[i]);
    if (pts[pts.length - 1] !== valuationHistory[valuationHistory.length - 1])
      pts.push(valuationHistory[valuationHistory.length - 1]);
    return pts.map((d) => ({ month: d.month, total: d.total }));
  }, [valuationHistory]);

  // X-axis ticks: one entry per unique year (avoid repeated labels)
  const yearTicks = useMemo(() => {
    const seen = new Set();
    const ticks = [];
    for (const d of chartData) {
      const y = (d.month || "").split("-")[0];
      if (y && !seen.has(y)) {
        seen.add(y);
        ticks.push(d.month);
      }
    }
    return ticks;
  }, [chartData]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[70vh] flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-[color:var(--bronze,#A8804E)] border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="-m-6 lg:-m-10" data-testid="dashboard-premium" style={{ background: C.offWhite }}>
        {/* ============ HERO ============ */}
        <section className="relative overflow-hidden" data-testid="dashboard-hero">
          <div className="relative h-[480px] lg:h-[620px] xl:h-[720px] 2xl:h-[820px] w-full" style={{ background: C.black }}>
            <img
              src={HERO_BRAND_IMAGE}
              alt="Groupe Kempf"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "center 78%" }}
            />
            {/* Left-side dark fade — narrower & lighter so the mountain stays visible on wide screens */}
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(15,15,14,0.72) 0%, rgba(15,15,14,0.35) 22%, rgba(15,15,14,0) 42%)" }} />
            {/* Bottom fade to blend into the KPI band */}
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,15,14,0) 60%, rgba(15,15,14,0.92) 100%)" }} />

            <motion.div
              initial="hidden" animate="show"
              className="relative h-full w-full px-8 lg:px-16 xl:px-20 pt-16 lg:pt-24 flex flex-col"
            >
              <motion.div variants={fadeUp} custom={0} className="max-w-[42%]">
                <p className="text-[10px] lg:text-xs tracking-[0.35em] uppercase" style={{ color: C.bronze }}>
                  {t("dashboard.eyebrow")}
                </p>
                <h1 className="mt-4 font-heading text-white text-5xl lg:text-7xl leading-[1.05] tracking-tight">
                  {t("dashboard.title_line1")}<br />
                  <span className="font-accent italic" style={{ color: C.stone }}>{t("dashboard.title_line2")}</span>
                </h1>

                {/* MiniStats row just below the title so they never collide with the KPI band overlap */}
                <div className="mt-10 flex items-end gap-8 lg:gap-12">
                  <MiniStat label={t("dashboard.mini_assets")} value={fmtInt(stats?.patrimonial_assets)} align="left" />
                  <MiniStat label={t("dashboard.mini_cities")} value={fmtInt(derived.cities.size)} align="left" />
                  <MiniStat label={t("dashboard.mini_sold")} value={fmtInt(stats?.sold_assets)} align="left" />
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Overlapping KPI band */}
          <div className="relative w-full px-8 lg:px-16 xl:px-20 -mt-24 lg:-mt-28 z-10">
            <motion.div
              initial="hidden" animate="show"
              className="grid grid-cols-2 lg:grid-cols-6"
              style={{ background: C.black, border: `1px solid ${C.mineral}40` }}
            >
              <KpiCell label={t("dashboard.kpi_value")} value={fmtEURcompact(derived.totalValuationFull)} sub={t("dashboard.kpi_value_sub")} idx={0} highlight />
              <KpiCell label={t("dashboard.kpi_ltv")} value={fmtPct(stats?.average_ltv)} sub={fmtEURcompact(totalDebt)} idx={1} />
              <KpiCell label={t("dashboard.kpi_equity")} value={fmtEURcompact(derived.totalEquity)} sub={t("dashboard.kpi_equity_sub")} idx={2} />
              <KpiCell label={t("dashboard.kpi_rent")} value={fmtEURcompact(derived.totalRentFull)} sub={t("dashboard.kpi_rent_sub")} idx={3} />
              <KpiCell label={t("dashboard.kpi_gains")} value={fmtEURcompact(derived.unrealizedGain)} sub={fmtPct(derived.unrealizedGainPct)} idx={4} positive={derived.unrealizedGain >= 0} />
              <KpiCell label={t("dashboard.kpi_yield")} value={fmtPct(commercialYield)} sub={t("dashboard.kpi_yield_sub")} idx={5} />
            </motion.div>
          </div>
        </section>

        {/* ============ LIVE RENT COUNTER ============ */}
        <LiveRentCounter assets={derived.patrimonial} />

        {/* ============ MAIN CONTENT ============ */}
        <div className="w-full px-8 lg:px-16 xl:px-20 py-20 lg:py-28 space-y-20 lg:space-y-24">

          {/* Portfolio composition + Valuation chart */}
          <motion.section
            initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8"
            data-testid="section-composition"
          >
            {/* Composition */}
            <motion.div variants={fadeUp} custom={0} className="lg:col-span-5 bg-white" style={{ border: `1px solid ${C.stoneDark}` }}>
              <div className="p-8 lg:p-10">
                <SectionTitle eyebrow={t("dashboard.composition_eyebrow")} title={t("dashboard.composition_title")} />
                <div className="mt-8 flex flex-col lg:flex-row items-center gap-8">
                  {poleData.length > 0 ? (
                    <>
                      <div className="w-40 h-40 flex-shrink-0">
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={poleData} dataKey="value" nameKey="pole"
                              innerRadius={45} outerRadius={78} paddingAngle={2} stroke="none"
                            >
                              {poleData.map((_, i) => (
                                <Cell key={i} fill={POLE_COLORS[i % POLE_COLORS.length]} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex-1 w-full space-y-3">
                        {poleData.slice(0, 6).map((p, i) => (
                          <div key={p.key} className="flex items-center justify-between text-sm" data-testid={`pole-row-${p.key}`}>
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: POLE_COLORS[i % POLE_COLORS.length] }} />
                              <span className="truncate" style={{ color: C.black }}>{p.pole}</span>
                              <span className="text-xs" style={{ color: C.mineralLight }}>· {p.count}</span>
                            </div>
                            <span className="font-heading tabular-nums" style={{ color: C.black }}>
                              {fmtEURcompact(p.value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm" style={{ color: C.mineral }}>{t("dashboard.composition_empty")}</p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Valuation chart */}
            <motion.div variants={fadeUp} custom={1} className="lg:col-span-7 bg-white" style={{ border: `1px solid ${C.stoneDark}` }}>
              <div className="p-8 lg:p-10">
                <SectionTitle eyebrow={t("dashboard.evolution_eyebrow")} title={t("dashboard.evolution_title")} />
                <div className="mt-8 h-64">
                  {chartData.length > 1 ? (
                    <ResponsiveContainer>
                      <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="bronzeLine" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor={C.bronzeDark} />
                            <stop offset="100%" stopColor={C.bronze} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke={C.stoneDark} strokeDasharray="2 6" vertical={false} />
                        <XAxis
                          dataKey="month" tick={{ fontSize: 10, fill: C.mineralLight }}
                          axisLine={{ stroke: C.stoneDark }} tickLine={false}
                          tickFormatter={(m) => (m ? m.split("-")[0] : "")}
                          ticks={yearTicks}
                          interval={0}
                        />
                        <YAxis
                          tick={{ fontSize: 10, fill: C.mineralLight }}
                          axisLine={false} tickLine={false}
                          tickFormatter={(v) => `${(v / 1e6).toLocaleString("de-DE")}M`}
                          width={40}
                        />
                        <Tooltip
                          contentStyle={{ background: C.black, border: `1px solid ${C.mineral}`, borderRadius: 0, color: "#fff" }}
                          labelStyle={{ color: C.stone, fontSize: 11, letterSpacing: 2, textTransform: "uppercase" }}
                          itemStyle={{ color: "#fff", fontSize: 13 }}
                          formatter={(v) => [fmtEUR(v), t("dashboard.evolution_tooltip")]}
                        />
                        <Line
                          type="monotone" dataKey="total"
                          stroke="url(#bronzeLine)" strokeWidth={2.5}
                          dot={false} activeDot={{ r: 5, fill: C.bronze, stroke: C.offWhite, strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm" style={{ color: C.mineral }}>
                      {t("dashboard.evolution_empty")}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.section>

          {/* Pipeline acquisitions */}
          {dossiers.length > 0 && (
            <motion.section
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              data-testid="section-pipeline"
            >
              <motion.div variants={fadeUp} custom={0}>
                <SectionTitle eyebrow={t("dashboard.pipeline_eyebrow")} title={t("dashboard.pipeline_title")} action={{ label: t("dashboard.pipeline_view_all"), onClick: () => navigate("/dossiers") }} />
              </motion.div>

              {/* Status summary counters (compact header row) */}
              <motion.div variants={fadeUp} custom={1} className="mt-8 grid grid-cols-4 gap-6 pb-4" style={{ borderBottom: `1px solid ${C.stoneDark}` }}>
                {STATUS_ORDER.map((st, idx) => (
                  <div key={st} className="flex items-baseline justify-between" data-testid={`pipeline-counter-${st}`}>
                    <span className="text-[10px] tracking-[0.3em] uppercase" style={{ color: C.mineral }}>
                      {statusLabel(st, t)}
                    </span>
                    <span className="font-heading text-2xl" style={{ color: idx === 3 ? C.bronze : C.black }}>
                      {pipeline[st].length}
                    </span>
                  </div>
                ))}
              </motion.div>

              {/* Horizontal row of all dossiers — they follow each other, never stack */}
              <motion.div
                variants={fadeUp} custom={2}
                className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                data-testid="pipeline-row"
              >
                {STATUS_ORDER.flatMap((st) => pipeline[st].map((d) => ({ ...d, _status: st }))).length === 0 ? (
                  <p className="text-sm italic col-span-full" style={{ color: C.mineralLight }}>Aucun dossier en cours.</p>
                ) : (
                  STATUS_ORDER.flatMap((st) => pipeline[st].map((d) => ({ ...d, _status: st }))).map((d) => {
                    const dPhoto = resolvePhoto(pickPhoto(d));
                    const targetVal = d.post_renovation_valuation || d.current_valuation || 0;
                    const isMDB = d.strategy === "marchand_de_bien";
                    // Total cost of ownership = sum of all employment items (acquisition + works + fees…)
                    const totalCost = (d.employment_items || []).reduce((s, it) => s + (Number(it.amount) || 0), 0);
                    // Commercial → target yield = market_rent (annual) / total cost
                    const yieldPct = !isMDB && d.market_rent > 0 && totalCost > 0
                      ? (d.market_rent / totalCost) * 100
                      : null;
                    // Marchand de bien → target value creation = target valuation − total cost
                    const valueCreation = isMDB && targetVal > 0 && totalCost > 0
                      ? targetVal - totalCost
                      : null;
                    const isActe = d._status === "acte";
                    return (
                      <button
                        key={d.id}
                        onClick={() => navigate(`/dossiers/${d.id}`)}
                        data-testid={`pipeline-card-${d.id}`}
                        className="text-left group transition-all hover:-translate-y-0.5 hover:shadow-lg overflow-hidden flex items-stretch h-44"
                        style={{ background: "#fff", border: `1px solid ${C.stoneDark}` }}
                      >
                        <div className="relative w-44 flex-shrink-0 overflow-hidden" style={{ background: C.stone }}>
                          {dPhoto ? (
                            <PhotoDisplay
                              url={dPhoto}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="font-accent italic text-2xl" style={{ color: C.mineralLight }}>—</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 p-5 flex flex-col justify-between gap-3 border-l" style={{ borderColor: C.stoneDark }}>
                          <div className="min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className="font-heading text-lg leading-tight" style={{ color: C.black }}><DT>{d.name}</DT></p>
                              <span
                                className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 whitespace-nowrap flex-shrink-0"
                                style={{
                                  background: isActe ? C.black : "transparent",
                                  color: isActe ? C.bronze : C.bronzeDark,
                                  border: isActe ? "none" : `1px solid ${C.bronzeDark}`,
                                }}
                                data-testid={`pipeline-card-status-${d.id}`}
                              >
                                {statusLabel(d._status, t)}
                              </span>
                            </div>
                            {d.address && (
                              <p className="text-xs mt-1 line-clamp-2" style={{ color: C.mineral }}><DT>{d.address}</DT></p>
                            )}
                          </div>
                          <div className="flex items-end justify-between gap-2 pt-2" style={{ borderTop: `1px solid ${C.stoneDark}` }}>
                            <div>
                              <p className="text-[9px] uppercase tracking-widest" style={{ color: C.mineralLight }}>{t("dashboard.pipeline_target_valuation")}</p>
                              <p className="font-heading text-lg tabular-nums whitespace-nowrap" style={{ color: C.black }}>
                                {fmtEURcompact(targetVal)}
                              </p>
                            </div>
                            {yieldPct !== null && (
                              <div className="text-right">
                                <p className="text-[9px] uppercase tracking-widest" style={{ color: C.mineralLight }}>{t("dashboard.pipeline_target_yield")}</p>
                                <p className="font-heading text-lg tabular-nums whitespace-nowrap" style={{ color: C.bronze }}>
                                  {fmtPct(yieldPct)}
                                </p>
                              </div>
                            )}
                            {valueCreation !== null && (
                              <div className="text-right">
                                <p className="text-[9px] uppercase tracking-widest" style={{ color: C.mineralLight }}>{t("dashboard.pipeline_target_value_creation")}</p>
                                <p className="font-heading text-lg tabular-nums whitespace-nowrap" style={{ color: C.bronze }}>
                                  {fmtEURcompact(valueCreation)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </motion.div>
            </motion.section>
          )}

          {/* Top strategic assets */}
          {derived.topAssets.length > 0 && (
            <motion.section
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              data-testid="section-top-assets"
            >
              <motion.div variants={fadeUp} custom={0}>
                <SectionTitle
                  eyebrow={t("dashboard.strategic_eyebrow")}
                  title={t("dashboard.strategic_title")}
                  action={{ label: t("dashboard.strategic_explore"), onClick: () => navigate("/assets") }}
                  extraAction={isAdmin ? {
                    label: t("dashboard.strategic_select"),
                    onClick: () => {
                      setStrategicDraft(new Set(assets.filter(a => a.is_strategic).map(a => a.id)));
                      setStrategicModalOpen(true);
                    },
                    testid: "select-strategic-btn",
                  } : null}
                />
              </motion.div>
              <motion.div variants={fadeUp} custom={1} className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {derived.topAssets.map((a) => {
                  // Display 100 % of the asset's values (no ownership weighting), consistent with dashboard KPIs
                  const val = a.current_valuation || 0;
                  const acq = a.acquisition_price || 0;
                  const gain = val - acq;
                  const gainPct = acq > 0 ? (gain / acq) * 100 : 0;
                  const rent = assetAnnualIncome(a);
                  const yieldPct = acq > 0 ? (rent / acq) * 100 : 0;
                  const photoUrl = resolvePhoto(pickPhoto(a));
                  return (
                    <button
                      key={a.id}
                      onClick={() => navigate(`/assets/${a.id}`)}
                      data-testid={`top-asset-${a.id}`}
                      className="group text-left bg-white overflow-hidden transition-all"
                      style={{ border: `1px solid ${C.stoneDark}` }}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden" style={{ background: C.stone }}>
                        {photoUrl ? (
                          <PhotoDisplay
                            url={photoUrl}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="font-accent italic text-3xl" style={{ color: C.mineralLight }}>—</span>
                          </div>
                        )}
                        {a.pole && (
                          <span
                            className="absolute top-4 left-4 text-[10px] tracking-widest uppercase px-2.5 py-1"
                            style={{ background: `${C.black}CC`, color: C.stone }}
                          >
                            {poleLabel(a.pole, t)}
                          </span>
                        )}
                      </div>
                      <div className="p-6">
                        <p className="font-heading text-lg leading-tight" style={{ color: C.black }}><DT>{a.name}</DT></p>
                        {(a.city || extractCity(a.address)) && (
                          <p className="text-xs mt-1" style={{ color: C.mineral }}><DT>{a.city || extractCity(a.address)}</DT></p>
                        )}
                        <div className="mt-5 pt-5 flex items-end justify-between" style={{ borderTop: `1px solid ${C.stoneDark}` }}>
                          <div>
                            <p className="text-[9px] uppercase tracking-widest" style={{ color: C.mineralLight }}>{t("dashboard.asset_valuation")}</p>
                            <p className="font-heading text-lg tabular-nums" style={{ color: C.black }}>{fmtEURcompact(val)}</p>
                          </div>
                          <div className="text-right">
                            {yieldPct > 0 ? (
                              <>
                                <p className="text-[9px] uppercase tracking-widest" style={{ color: C.mineralLight }}>{t("dashboard.asset_yield")}</p>
                                <p className="font-heading text-lg tabular-nums" style={{ color: C.bronze }}>{fmtPct(yieldPct)}</p>
                              </>
                            ) : gain !== 0 ? (
                              <>
                                <p className="text-[9px] uppercase tracking-widest" style={{ color: C.mineralLight }}>{t("dashboard.asset_gain")}</p>
                                <p className="font-heading text-lg tabular-nums" style={{ color: gain >= 0 ? C.bronze : "#b34a4a" }}>
                                  {gainPct >= 0 ? "+" : ""}{fmtPct(gainPct)}
                                </p>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </motion.div>
            </motion.section>
          )}

          {/* Footprint */}
          {derived.cities.size > 0 && (
            <motion.section
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              data-testid="section-footprint"
              className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 pt-8 lg:pt-16 mt-4 lg:mt-8"
            >
              <motion.div variants={fadeUp} custom={0} className="lg:col-span-8 relative overflow-hidden" style={{ background: C.black, minHeight: 260 }}>
                <div className="p-10 lg:p-14 relative z-10">
                  <p className="text-[10px] tracking-[0.35em] uppercase" style={{ color: C.bronze }}>{t("dashboard.footprint_eyebrow")}</p>
                  <h3 className="mt-4 font-heading text-white text-4xl lg:text-5xl leading-none">
                    {fmtInt(derived.cities.size)} <span className="font-accent italic" style={{ color: C.stone }}>{t("dashboard.footprint_cities")}</span>
                  </h3>
                  <p className="mt-6 text-sm max-w-md" style={{ color: C.stoneDark }}>
                    {t("dashboard.footprint_description", { count: fmtInt(derived.cities.size), assets: fmtInt(stats?.patrimonial_assets), valuation: fmtEURcompact(derived.totalValuationFull) })}
                  </p>
                  <button
                    onClick={() => navigate("/map")}
                    className="mt-8 inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase transition-colors hover:opacity-80"
                    style={{ color: C.bronze }}
                    data-testid="cta-explore-map"
                  >
                    {t("dashboard.footprint_cta")}
                    <svg width="18" height="8" viewBox="0 0 18 8" fill="none"><path d="M1 4h16m0 0L13 1m4 3l-4 3" stroke="currentColor" strokeWidth="1"/></svg>
                  </button>
                </div>
                {/* Decorative subtle grid */}
                <div className="absolute inset-0 pointer-events-none opacity-20"
                  style={{ backgroundImage: `linear-gradient(${C.mineral} 1px, transparent 1px), linear-gradient(90deg, ${C.mineral} 1px, transparent 1px)`, backgroundSize: "48px 48px" }}
                />
              </motion.div>

              <motion.div variants={fadeUp} custom={1} className="lg:col-span-4 grid grid-cols-1 gap-4">
                <StackedStat label={t("dashboard.stat_sold_historic")} value={fmtInt(stats?.sold_assets || 0)} sub={t("dashboard.stat_sold_sub")} />
                <StackedStat label={t("dashboard.stat_dossiers_open")} value={fmtInt(dossiers.length)} sub={t("dashboard.stat_dossiers_sub")} />
                <StackedStat label={t("dashboard.stat_active_assets")} value={fmtInt(stats?.patrimonial_assets || 0)} sub={t("dashboard.stat_active_sub")} />
              </motion.div>
            </motion.section>
          )}
        </div>
      </div>

      {/* Strategic assets selector modal */}
      {strategicModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(15,15,14,0.7)", backdropFilter: "blur(4px)" }}
          onClick={() => !savingStrategic && setStrategicModalOpen(false)}
          data-testid="strategic-modal"
        >
          <div
            className="w-full max-w-3xl bg-white flex flex-col"
            style={{ maxHeight: "85vh", border: `1px solid ${C.stoneDark}` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 border-b" style={{ borderColor: C.stoneDark }}>
              <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: C.bronze }}>Configuration</p>
              <h3 className="mt-2 font-heading text-3xl" style={{ color: C.black }}>
                Sélectionner les actifs stratégiques
              </h3>
              <p className="mt-3 text-sm" style={{ color: C.mineral }}>
                Cochez les actifs à mettre en avant sur le tableau de bord.
                Si aucun n&apos;est coché, les 6 actifs les mieux valorisés sont utilisés par défaut.
                <span className="ml-2 font-heading" style={{ color: C.black }}>
                  {strategicDraft.size} sélectionné{strategicDraft.size > 1 ? "s" : ""}
                </span>
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {assets
                .filter((a) => a.status === "patrimonial" || a.status === "marchand_de_bien")
                .map((a) => {
                  const checked = strategicDraft.has(a.id);
                  const val = a.current_valuation || 0;
                  return (
                    <label
                      key={a.id}
                      data-testid={`strategic-row-${a.id}`}
                      className="flex items-center gap-4 p-3 cursor-pointer transition-colors hover:bg-[color:var(--kempf-stone,#E8E1D5)]"
                      style={{ border: `1px solid ${checked ? C.bronze : "transparent"}`, background: checked ? "#FAF5EC" : "transparent" }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStrategicDraft(a.id)}
                        className="w-4 h-4 accent-[color:var(--kempf-bronze,#A8804E)]"
                        data-testid={`strategic-checkbox-${a.id}`}
                      />
                      <div className="w-14 h-14 flex-shrink-0 overflow-hidden" style={{ background: C.stone }}>
                        {a.main_photo ? (
                          <PhotoDisplay url={resolvePhoto(a.main_photo)} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="font-accent italic text-lg" style={{ color: C.mineralLight }}>—</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading text-base truncate" style={{ color: C.black }}>{a.name}</p>
                        <p className="text-xs truncate" style={{ color: C.mineral }}>
                          {poleLabel(a.pole, t)}{a.address ? ` · ${extractCity(a.address)}` : ""}
                        </p>
                      </div>
                      <p className="font-heading text-sm tabular-nums whitespace-nowrap" style={{ color: C.black }}>
                        {fmtEURcompact(val)}
                      </p>
                    </label>
                  );
                })}
            </div>
            <div className="p-6 border-t flex items-center justify-between gap-4" style={{ borderColor: C.stoneDark, background: C.offWhite }}>
              <button
                onClick={() => setStrategicDraft(new Set())}
                disabled={savingStrategic}
                className="text-xs tracking-[0.25em] uppercase hover:opacity-70 disabled:opacity-40"
                style={{ color: C.mineral }}
                data-testid="strategic-clear-btn"
              >
                Tout décocher
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setStrategicModalOpen(false)}
                  disabled={savingStrategic}
                  className="px-6 py-2.5 text-xs tracking-[0.25em] uppercase hover:bg-[color:var(--kempf-stone,#E8E1D5)]"
                  style={{ border: `1px solid ${C.stoneDark}`, color: C.mineral }}
                  data-testid="strategic-cancel-btn"
                >
                  Annuler
                </button>
                <button
                  onClick={saveStrategicSelection}
                  disabled={savingStrategic}
                  className="px-6 py-2.5 text-xs tracking-[0.25em] uppercase text-white disabled:opacity-50"
                  style={{ background: C.black }}
                  data-testid="strategic-save-btn"
                >
                  {savingStrategic ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

// ============================================================
// Sub-components
// ============================================================
function MiniStat({ label, value, align = "right" }) {
  return (
    <div className={align === "right" ? "text-right" : "text-left"}>
      <p className="text-[9px] tracking-[0.3em] uppercase" style={{ color: C.bronze }}>{label}</p>
      <p className="font-heading text-white text-2xl mt-1 tabular-nums">{value}</p>
    </div>
  );
}

function KpiCell({ label, value, sub, idx = 0, highlight = false, positive }) {
  return (
    <motion.div
      variants={fadeUp} custom={idx}
      className="p-6 lg:p-8 relative"
      style={{
        borderRight: `1px solid ${C.mineral}30`,
        borderBottom: `1px solid ${C.mineral}30`,
      }}
      data-testid={`kpi-${idx}`}
    >
      {highlight && (
        <span className="absolute top-0 left-0 w-8 h-[2px]" style={{ background: C.bronze }} />
      )}
      <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: C.mineralLight }}>{label}</p>
      <p className="mt-4 font-heading text-white text-3xl lg:text-4xl tabular-nums leading-none">
        {value}
      </p>
      {sub && (
        <p className="mt-3 text-[11px] tabular-nums" style={{ color: positive === false ? "#c86b6b" : C.stoneDark }}>
          {sub}
        </p>
      )}
    </motion.div>
  );
}

function SectionTitle({ eyebrow, title, action, extraAction }) {
  return (
    <div className="flex items-end justify-between gap-6">
      <div>
        <p className="text-[10px] tracking-[0.35em] uppercase" style={{ color: C.bronze }}>{eyebrow}</p>
        <h2 className="mt-3 font-heading text-3xl lg:text-4xl" style={{ color: C.black }}>{title}</h2>
      </div>
      <div className="hidden sm:flex items-center gap-6">
        {extraAction && (
          <button
            onClick={extraAction.onClick}
            data-testid={extraAction.testid || "section-extra-action"}
            className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase transition-colors hover:opacity-70"
            style={{ color: C.black, borderBottom: `1px solid ${C.bronze}`, paddingBottom: 2 }}
          >
            {extraAction.label}
          </button>
        )}
        {action && (
          <button
            onClick={action.onClick}
            className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase transition-colors hover:opacity-70"
            style={{ color: C.bronzeDark }}
          >
            {action.label}
            <svg width="14" height="8" viewBox="0 0 14 8" fill="none"><path d="M1 4h12m0 0L10 1m3 3l-3 3" stroke="currentColor" strokeWidth="1"/></svg>
          </button>
        )}
      </div>
    </div>
  );
}

function StackedStat({ label, value, sub }) {
  return (
    <div className="p-6 bg-white flex items-center justify-between" style={{ border: `1px solid ${C.stoneDark}` }}>
      <div>
        <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: C.mineral }}>{label}</p>
        <p className="mt-1 text-[11px]" style={{ color: C.mineralLight }}>{sub}</p>
      </div>
      <p className="font-heading text-3xl tabular-nums" style={{ color: C.black }}>{value}</p>
    </div>
  );
}

// ============================================================
// Live rent counter — increments every second based on annual rent
// ============================================================
function LiveRentCounter({ assets }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(0);

  // Compute annual rent + acquisition date for each asset (per-pole to avoid double count)
  const rentAssets = useMemo(() => (assets || []).map((a) => ({
    annualRent: assetAnnualIncome(a),
    acquisitionDate: a.acquisition_date || null,
  })).filter((x) => x.annualRent > 0), [assets]);

  // Total annualized rent for assets already acquired (skip future acquisitions)
  const totalAnnualRent = useMemo(() => {
    const now = new Date();
    return rentAssets.reduce((sum, x) => {
      const acq = x.acquisitionDate ? new Date(x.acquisitionDate) : null;
      if (acq && !isNaN(acq) && acq > now) return sum;
      return sum + x.annualRent;
    }, 0);
  }, [rentAssets]);

  useEffect(() => {
    if (!totalAnnualRent) return undefined;
    const compute = () => {
      const now = new Date();
      const year = now.getFullYear();
      const startOfYear = new Date(year, 0, 1, 0, 0, 0, 0);
      const endOfYear = new Date(year + 1, 0, 1, 0, 0, 0, 0);
      const yearMs = endOfYear - startOfYear;
      const elapsedMs = now - startOfYear;
      return (totalAnnualRent * elapsedMs) / yearMs;
    };
    setAmount(compute());
    const id = setInterval(() => setAmount(compute()), 1000);
    return () => clearInterval(id);
  }, [totalAnnualRent]);

  const fmt = new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const perSecond = totalAnnualRent ? totalAnnualRent / (365 * 24 * 3600) : 0;
  const now = new Date();
  const year = now.getFullYear();
  const startOfYear = new Date(year, 0, 1);
  const dayOfYear = Math.floor((now - startOfYear) / 86400000) + 1;
  const daysInYear = ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) ? 366 : 365;
  const yearProgressPct = (dayOfYear / daysInYear) * 100;

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="relative overflow-hidden"
      data-testid="live-rent-counter"
      style={{ background: C.offWhite }}
    >
      {/* Top bronze hairline */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.bronze}80 20%, ${C.bronze}80 80%, transparent 100%)` }} />
      {/* Bottom bronze hairline */}
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.stoneDark} 20%, ${C.stoneDark} 80%, transparent 100%)` }} />

      <div className="relative w-full px-8 lg:px-16 xl:px-20 py-8 lg:py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <div className="flex items-center gap-3">
            <span className="inline-block w-6 h-px" style={{ background: C.bronze }} />
            <p className="text-[9px] tracking-[0.35em] uppercase" style={{ color: C.bronze }}>
              {t("dashboard.live_rent_eyebrow", { year })}
            </p>
          </div>
          <p
            className="mt-3 font-heading tabular-nums leading-none"
            style={{
              fontSize: "clamp(2rem, 3.5vw, 3.5rem)",
              letterSpacing: "-0.02em",
              color: C.black,
            }}
            data-testid="live-rent-value"
          >
            {fmt.format(amount)}
            <span className="font-accent italic ml-2" style={{ color: C.bronze, fontSize: "0.55em" }}>€</span>
          </p>
        </div>

        <div className="grid grid-cols-3 gap-x-8 gap-y-3">
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase" style={{ color: C.mineralLight }}>{t("dashboard.live_rent_day")}</p>
            <p className="mt-1 font-heading text-base tabular-nums" style={{ color: C.black }}>
              {dayOfYear} <span className="text-xs" style={{ color: C.mineral }}>/ {daysInYear}</span>
              <span className="text-xs ml-2" style={{ color: C.bronze }}>{yearProgressPct.toFixed(1)} %</span>
            </p>
          </div>
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase" style={{ color: C.mineralLight }}>{t("dashboard.live_rent_rate")}</p>
            <p className="mt-1 font-heading text-base tabular-nums" style={{ color: C.black }}>
              {fmt.format(perSecond)}
              <span className="text-xs ml-1" style={{ color: C.bronze }}>€ / s</span>
            </p>
          </div>
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase" style={{ color: C.mineralLight }}>{t("dashboard.live_rent_total")}</p>
            <p className="mt-1 font-heading text-base tabular-nums" style={{ color: C.black }}>
              {new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(totalAnnualRent)}
              <span className="text-xs ml-1" style={{ color: C.bronze }}>€</span>
            </p>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
